import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/shared/ui/button";

import { getSubscriptionOrdersQueryOptions } from "./query-options";
import type { SubscriptionOrderRecord } from "./types";

const PAGE_SIZE = 10;

function formatDate(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium" }).format(date);
}

function formatAmount(order: SubscriptionOrderRecord) {
  if (order.type === "education_grant") return "免费赠送";
  const amount = Number(order.amount);
  if (!Number.isFinite(amount)) return "—";
  const value = amount / 100;
  return order.currency === "CNY" ? `¥${value.toFixed(2)}` : `${value.toFixed(2)} ${order.currency}`;
}

function formatStatus(order: SubscriptionOrderRecord) {
  if (order.type === "education_grant") return "已发放";
  return ({ paid: "已支付", pending: "待支付", closed: "已关闭", failed: "失败" } as Record<string, string>)[order.status] ?? order.status;
}

export function SubscriptionOrders({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const { data, isError, isLoading, isFetching, refetch } = useQuery({
    ...getSubscriptionOrdersQueryOptions(userId, page),
    enabled: Boolean(userId),
  });
  const orders = data?.orders ?? [];

  return (
    <div className="border-t border-border/50 pt-5">
      <h3 className="text-sm font-medium">订阅记录</h3>
      {isLoading ? <p className="py-5 text-sm text-muted-foreground">正在加载…</p> : null}
      {isError ? <div className="flex items-center gap-2 py-5 text-sm text-muted-foreground">
        暂时无法获取订阅记录。
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>重试</Button>
      </div> : null}
      {data && orders.length === 0 && page === 1 ? <p className="py-5 text-sm text-muted-foreground">暂无订阅记录。</p> : null}
      {data && orders.length > 0 ? <ul className="mt-3 divide-y divide-border/50">
        {orders.map((order) => <li key={order.id} className="flex items-start justify-between gap-4 py-3 text-sm">
          <div className="min-w-0">
            <p className="font-medium">{order.plan_name || order.plan_code}{order.type === "education_grant" ? " · 教育赠送" : ""}</p>
            <p className="mt-1 text-xs text-muted-foreground">{formatDate(order.created_at)} · {order.order_no}</p>
            {order.type === "education_grant" && order.expires_at ? <p className="mt-1 text-xs text-muted-foreground">本次权益至 {formatDate(order.expires_at)}</p> : null}
          </div>
          <div className="shrink-0 text-right">
            <p>{formatAmount(order)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{formatStatus(order)}</p>
          </div>
        </li>)}
      </ul> : null}
      {page > 1 || orders.length === PAGE_SIZE ? <div className="mt-3 flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" disabled={page === 1 || isFetching} onClick={() => setPage(page - 1)}>上一页</Button>
        <span className="text-xs text-muted-foreground">第 {page} 页</span>
        <Button type="button" variant="outline" size="sm" disabled={orders.length < PAGE_SIZE || isFetching} onClick={() => setPage(page + 1)}>下一页</Button>
      </div> : null}
    </div>
  );
}
